import { nextTick, onMounted, onUnmounted, type Ref } from 'vue';

const LISTING_ARTICLE_SCROLL_STORAGE_KEY = 'listing_article_scroll_data_mfe';
const LISTING_ARTICLE_SCROLL_MAX_AGE = 5 * 60 * 1000;
const LISTING_ARTICLE_SCROLL_MAX_WAIT = 30 * 1000;
const LISTING_ARTICLE_SCROLL_RETRY_DELAY = 100;
const LISTING_ARTICLE_SCROLL_STABLE_WAIT = 1000;
const LISTING_ARTICLE_SCROLL_TOLERANCE = 4;

interface ListingArticleScrollData {
  articleHref?: string;
  articleTop?: number;
  locationKey: string;
  position: number;
  timestamp: number;
  scrollHeight: number;
}

interface UseListingArticleScrollRestorationOptions {
  containerRef: Ref<HTMLElement | null>;
}

interface ListingArticleRestoreState {
  isDisposed: boolean;
  containerRef: Ref<HTMLElement | null>;
  timeoutId: number | null;
}

// Возвращает ключ текущей страницы листинга с учетом query- и hash-параметров.
const getCurrentLocationKey = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

// Проверяет, что значение из sessionStorage соответствует сохраненной позиции скролла.
const isListingArticleScrollData = (value: unknown): value is ListingArticleScrollData => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  if (!('locationKey' in value) || !('position' in value) || !('timestamp' in value) || !('scrollHeight' in value)) {
    return false;
  }

  return (
    (!('articleHref' in value) || typeof value.articleHref === 'string') &&
    (!('articleTop' in value) || typeof value.articleTop === 'number') &&
    typeof value.locationKey === 'string' &&
    typeof value.position === 'number' &&
    typeof value.timestamp === 'number' &&
    typeof value.scrollHeight === 'number'
  );
};

// Читает сохраненную позицию скролла для возврата из статьи.
const readSavedScrollData = (): ListingArticleScrollData | null => {
  const rawScrollData = sessionStorage.getItem(LISTING_ARTICLE_SCROLL_STORAGE_KEY);

  if (!rawScrollData) {
    return null;
  }

  try {
    const parsedScrollData: unknown = JSON.parse(rawScrollData);

    return isListingArticleScrollData(parsedScrollData) ? parsedScrollData : null;
  } catch {
    return null;
  }
};

// Очищает сохраненную позицию после успешного или просроченного восстановления.
const clearSavedScrollData = (): void => {
  sessionStorage.removeItem(LISTING_ARTICLE_SCROLL_STORAGE_KEY);
};

// Проверяет, что текущая загрузка страницы является обычным обновлением.
const isReloadNavigation = (): boolean => {
  const navigationEntry = performance.getEntriesByType('navigation')[0];

  return Boolean(navigationEntry && 'type' in navigationEntry && navigationEntry.type === 'reload');
};

// Сохраняет позицию перед переходом из листинга в статью.
const saveCurrentScrollData = (anchor: HTMLAnchorElement): void => {
  const anchorRect = anchor.getBoundingClientRect();
  const scrollData: ListingArticleScrollData = {
    articleHref: anchor.href,
    articleTop: anchorRect.top,
    locationKey: getCurrentLocationKey(),
    position: window.scrollY,
    timestamp: Date.now(),
    scrollHeight: document.documentElement.scrollHeight,
  };

  sessionStorage.setItem(LISTING_ARTICLE_SCROLL_STORAGE_KEY, JSON.stringify(scrollData));
};

// Находит ссылку, по которой произошел клик внутри блока статей.
const getClickedAnchor = (event: MouseEvent): HTMLAnchorElement | null => {
  for (const target of event.composedPath()) {
    if (target instanceof HTMLAnchorElement) {
      return target;
    }

    if (target instanceof Element) {
      const anchor = target.closest('a[href]');

      if (anchor instanceof HTMLAnchorElement) {
        return anchor;
      }
    }
  }

  return null;
};

// Проверяет, что ссылка ведет на статью журнала в текущем origin.
const isJournalArticleLink = (anchor: HTMLAnchorElement): boolean => {
  const articleUrl = new URL(anchor.href, window.location.href);

  return articleUrl.origin === window.location.origin && articleUrl.pathname.startsWith('/journal/articles/');
};

// Отсекает клики, которые не должны менять состояние текущей вкладки.
const shouldSaveScrollBeforeNavigation = (event: MouseEvent, anchor: HTMLAnchorElement): boolean => {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return false;
  }

  if (anchor.target && anchor.target !== '_self') {
    return false;
  }

  return !anchor.hasAttribute('download') && isJournalArticleLink(anchor);
};

// Обрабатывает клики по статьям и сохраняет позицию только перед обычной навигацией.
const handleArticleClick = (event: MouseEvent): void => {
  const anchor = getClickedAnchor(event);

  if (!anchor || !shouldSaveScrollBeforeNavigation(event, anchor)) {
    return;
  }

  saveCurrentScrollData(anchor);
};

// Сбрасывает отложенную попытку восстановления скролла.
const clearRestoreTimeout = (state: ListingArticleRestoreState): void => {
  if (state.timeoutId === null) {
    return;
  }

  window.clearTimeout(state.timeoutId);
  state.timeoutId = null;
};

// Проверяет, что ленивый блок статей уже отрендерил ссылки журнала.
const hasRenderedArticleLinks = (container: HTMLElement | null): boolean => {
  return Boolean(container?.querySelector('a[href*="/journal/articles/"]'));
};

// Вводит ленивый блок статей в область видимости, чтобы IntersectionObserver загрузил слот.
const requestArticlesBlockRender = (state: ListingArticleRestoreState): void => {
  if (!state.containerRef.value) {
    return;
  }

  state.containerRef.value.scrollIntoView({
    block: 'center',
    behavior: 'auto',
  });
};

// Ищет сохраненную ссылку статьи после повторного рендера ленивого блока.
const getSavedArticleAnchor = (
  scrollData: ListingArticleScrollData,
  container: HTMLElement | null,
): HTMLAnchorElement | null => {
  if (!scrollData.articleHref || !container) {
    return null;
  }

  for (const anchor of container.querySelectorAll('a[href*="/journal/articles/"]')) {
    if (anchor instanceof HTMLAnchorElement && anchor.href === scrollData.articleHref) {
      return anchor;
    }
  }

  return null;
};

// Рассчитывает позицию скролла по сохраненному экранному положению ссылки статьи.
const getRestoreTargetPosition = (
  scrollData: ListingArticleScrollData,
  state: ListingArticleRestoreState,
  maxScrollPosition: number,
): number => {
  const savedAnchor = getSavedArticleAnchor(scrollData, state.containerRef.value);

  if (!savedAnchor || typeof scrollData.articleTop !== 'number') {
    return Math.min(scrollData.position, maxScrollPosition);
  }

  const anchorRect = savedAnchor.getBoundingClientRect();
  const anchorTargetPosition = window.scrollY + anchorRect.top - scrollData.articleTop;

  return Math.min(Math.max(0, anchorTargetPosition), maxScrollPosition);
};

// Повторяет скролл до сохраненной позиции, пока ленивые блоки листинга не восстановят высоту страницы.
const restoreScrollPosition = (
  scrollData: ListingArticleScrollData,
  state: ListingArticleRestoreState,
  startedAt = Date.now(),
  stableStartedAt = 0,
): void => {
  if (state.isDisposed) {
    return;
  }

  const currentTime = Date.now();
  const scrollElement = document.documentElement;
  const isArticlesBlockRendered = hasRenderedArticleLinks(state.containerRef.value);
  const maxScrollPosition = Math.max(0, scrollElement.scrollHeight - window.innerHeight);
  const savedAnchor = getSavedArticleAnchor(scrollData, state.containerRef.value);
  const targetPosition = getRestoreTargetPosition(scrollData, state, maxScrollPosition);

  const isHeightReady =
    scrollElement.scrollHeight >= scrollData.scrollHeight ||
    maxScrollPosition + LISTING_ARTICLE_SCROLL_TOLERANCE >= scrollData.position;

  if (!isArticlesBlockRendered) {
    requestArticlesBlockRender(state);
  } else {
    window.scrollTo({
      top: targetPosition,
      behavior: 'auto',
    });
  }

  const isAnchorPositionReady = Math.abs(window.scrollY - targetPosition) <= LISTING_ARTICLE_SCROLL_TOLERANCE;
  const isPageReady = isArticlesBlockRendered && (Boolean(savedAnchor) || isHeightReady);
  const nextStableStartedAt = isPageReady && isAnchorPositionReady ? stableStartedAt || currentTime : 0;

  if (nextStableStartedAt && currentTime - nextStableStartedAt >= LISTING_ARTICLE_SCROLL_STABLE_WAIT) {
    clearSavedScrollData();
    clearRestoreTimeout(state);

    return;
  }

  if (currentTime - startedAt >= LISTING_ARTICLE_SCROLL_MAX_WAIT) {
    clearSavedScrollData();
    clearRestoreTimeout(state);

    return;
  }

  clearRestoreTimeout(state);
  state.timeoutId = window.setTimeout(() => {
    restoreScrollPosition(scrollData, state, startedAt, nextStableStartedAt);
  }, LISTING_ARTICLE_SCROLL_RETRY_DELAY);
};

// Запускает восстановление, если сохраненная позиция относится к текущему листингу.
const restoreSavedScrollData = async (state: ListingArticleRestoreState): Promise<void> => {
  if (isReloadNavigation()) {
    clearSavedScrollData();

    return;
  }

  const scrollData = readSavedScrollData();

  if (!scrollData) {
    return;
  }

  if (Date.now() - scrollData.timestamp > LISTING_ARTICLE_SCROLL_MAX_AGE) {
    clearSavedScrollData();

    return;
  }

  if (scrollData.locationKey !== getCurrentLocationKey()) {
    return;
  }

  await nextTick();
  restoreScrollPosition(scrollData, state);
};

// Подключает восстановление скролла для переходов из блока статей листинга.
export const useListingArticleScrollRestoration = ({
  containerRef,
}: UseListingArticleScrollRestorationOptions): void => {
  const restoreState: ListingArticleRestoreState = {
    containerRef,
    isDisposed: false,
    timeoutId: null,
  };

  // Устанавливает обработчик на стабильный контейнер блока статей.
  const addArticleClickListener = (): void => {
    containerRef.value?.addEventListener('click', handleArticleClick, true);
  };

  // Снимает обработчик, чтобы не оставить подписки после размонтирования блока.
  const removeArticleClickListener = (): void => {
    containerRef.value?.removeEventListener('click', handleArticleClick, true);
  };

  onMounted(() => {
    addArticleClickListener();
    void restoreSavedScrollData(restoreState);
  });

  onUnmounted(() => {
    restoreState.isDisposed = true;
    clearRestoreTimeout(restoreState);
    removeArticleClickListener();
  });
};
